import { Sorter } from '@flakiness/server/common/pagedResponse.js';
import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { humanReadableBytes, humanReadableInteger } from '@flakiness/shared/common/utils.js';
import { html } from 'lit';
import { Temporal } from 'temporal-polyfill';
import { DataColumn, DataTableProvider } from '../components/data-table.js';
import { PageProjectUsage } from '../page-project-usage.js';

type ProjectSortAxis = 'name' | 'totalBytes' | 'reportBytes' | 'attachmentBytes' | 'runsCount' | 'testRunsCount' | 'activity';

const totalStorage = (project: WireTypes.Project) => (project.snapshot?.reportBytes ?? 0) + (project.snapshot?.attachmentBytes ?? 0);

export function createProjectsDataProvider(projects: WireTypes.Project[]): DataTableProvider<WireTypes.Project, ProjectSortAxis> {
  // Snapshots can outlive their window when a project stops receiving uploads.
  // Count only the days the sparkline actually displays.
  const recentRuns = new Map(projects.map(project => {
    const today = Temporal.Now.plainDateISO(project.timezone);
    const since = today.subtract({ days: 30 }).toString();
    const until = today.toString();
    const runs = (project.snapshot?.sparkline ?? []).reduce((total, metric) =>
      total + (metric.day >= since && metric.day <= until ? metric.runsCount : 0), 0);
    return [project, runs] as const;
  }));

  const columns = {
    name: {
      sortAxis: 'name',
      sortOrder: ['asc', 'desc'],
      width: 'minmax(180px, 1fr)',
      renderHeader: () => html`Project`,
      renderElement: project => html`
        <a style="padding: var(--sl-spacing-small) 0; overflow: hidden; text-overflow: ellipsis;"
          title=${project.projectSlug}
          href=${PageProjectUsage.url({ orgSlug: project.org.orgSlug, projectSlug: project.projectSlug, snapshotId: undefined })}
        >${project.projectSlug}</a>
      `,
    },
    totalBytes: {
      sortAxis: 'totalBytes',
      renderHeader: () => html`Total storage`,
      renderElement: project => html`
        <span title=${`${totalStorage(project).toLocaleString()} bytes`}>${humanReadableBytes(totalStorage(project))}</span>
      `,
    },
    reportBytes: {
      sortAxis: 'reportBytes',
      renderHeader: () => html`Report storage`,
      renderElement: project => html`
        <span title=${`${(project.snapshot?.reportBytes ?? 0).toLocaleString()} bytes`}>${humanReadableBytes(project.snapshot?.reportBytes ?? 0)}</span>
      `,
    },
    attachmentBytes: {
      sortAxis: 'attachmentBytes',
      renderHeader: () => html`Attachment storage`,
      renderElement: project => html`
        <span title=${`${(project.snapshot?.attachmentBytes ?? 0).toLocaleString()} bytes`}>${humanReadableBytes(project.snapshot?.attachmentBytes ?? 0)}</span>
      `,
    },
    runsCount: {
      sortAxis: 'runsCount',
      renderHeader: () => html`Reports`,
      renderElement: project => html`
        <span title=${(project.snapshot?.runsCount ?? 0).toLocaleString()}>${humanReadableInteger(project.snapshot?.runsCount ?? 0)}</span>
      `,
    },
    testRunsCount: {
      sortAxis: 'testRunsCount',
      renderHeader: () => html`Test results`,
      renderElement: project => html`
        <span title=${(project.snapshot?.testRunsCount ?? 0).toLocaleString()}>${humanReadableInteger(project.snapshot?.testRunsCount ?? 0)}</span>
      `,
    },
    activity: {
      sortAxis: 'activity',
      renderHeader: () => html`Report uploads (30 days)`,
      renderElement: project => html`
        <sl-tooltip hoist content=${`Report uploads in the last 30 days: ${recentRuns.get(project)!.toLocaleString()}`}>
          <sparkline-chart
            style="width: 120px; height: 25px;"
            .timezone=${project.timezone}
            .metrics=${project.snapshot?.sparkline ?? []}
          ></sparkline-chart>
        </sl-tooltip>
      `,
    },
  } satisfies Record<ProjectSortAxis, DataColumn<WireTypes.Project, ProjectSortAxis>>;

  const compareName = (a: WireTypes.Project, b: WireTypes.Project) => a.projectSlug.localeCompare(b.projectSlug);
  const sorter = new Sorter<WireTypes.Project, ProjectSortAxis>([...projects], {
    name: compareName,
    totalBytes: (a, b) => totalStorage(a) - totalStorage(b) || compareName(a, b),
    reportBytes: (a, b) => (a.snapshot?.reportBytes ?? 0) - (b.snapshot?.reportBytes ?? 0) || compareName(a, b),
    attachmentBytes: (a, b) => (a.snapshot?.attachmentBytes ?? 0) - (b.snapshot?.attachmentBytes ?? 0) || compareName(a, b),
    runsCount: (a, b) => (a.snapshot?.runsCount ?? 0) - (b.snapshot?.runsCount ?? 0) || compareName(a, b),
    testRunsCount: (a, b) => (a.snapshot?.testRunsCount ?? 0) - (b.snapshot?.testRunsCount ?? 0) || compareName(a, b),
    activity: (a, b) => recentRuns.get(a)! - recentRuns.get(b)! || compareName(a, b),
  });
  return {
    columns: Object.values(columns),
    defaultSortColumn: columns.totalBytes,
    loader: async (pageOptions, sortOptions) => sorter.page(pageOptions, sortOptions?.axis ?? 'totalBytes', sortOptions?.direction ?? 'desc'),
  };
}
